import { Option } from '~/components/Select';
import countries from '~/constants/countries';
import { B13AFilingOptionMap } from '~/constants/customsDeclaration';
import Address, { isMilitaryAddress, isUSTerritory } from './Address';
import APIAddress from './APIAddress';
import { WebCustomsItem } from './CustomsItem';

type JSONCustomsDeclarationExporterIdentificationTaxId = {
  type: string;
  number: string;
};

type JSONCustomsDeclarationExporterIdentification = {
  eori_number: string;
  tax_id: JSONCustomsDeclarationExporterIdentificationTaxId;
};

type JSONCustomsDeclaration = {
  id: number;
  object_state: string;
  exporter_reference: string;
  importer_reference: string;
  contents_type: string;
  contents_explanation: string;
  invoice: string;
  notes: string;
  license: string;
  certificate: string;
  eel_pfc: string;
  aes_itn: string;
  non_delivery_option: string;
  is_certify: boolean;
  certify_signer: string;
  disclaimer: string;
  incoterm: string;
  metadata: string;
  is_commercial_invoice: boolean;
  is_test: boolean;
  b13a_filing_option: string;
  b13a_number: string;
  exporter_identification: JSONCustomsDeclarationExporterIdentification;
};

type CustomsDeclaration = {
  id: number;
  objectState?: string;
  exporterReference: string;
  importerReference: string;
  contentsType: string;
  contentsExplanation?: string;
  invoice: string;
  notes: string;
  license: string;
  certificate: string;
  eelPfc: string;
  aesItn?: string;
  nonDeliveryOption: string;
  isCertify: boolean;
  certifySigner: string;
  disclaimer?: string;
  incoterm: string;
  metadata?: string;
  isCommercialInvoice?: boolean;
  isTest?: boolean;
  b13aFilingOption?: string;
  b13aNumber?: string;
  eoriNumber?: string;
  taxIdType?: string;
  taxIdNumber?: string;
};

type CustomsDeclarationPayload = {
  id: number;
  contentsType: Option;
  contentsExplanation?: string;
  incoterm: Option;
  exporterReference: string;
  importerReference: string;
  invoice: string;
  nonDeliveryOption: Option;
  license: string;
  certificate: string;
  eelPfc: Option;
  aesItn?: string;
  certifySigner: string;
  notes: string;
  isCertify: boolean;
  b13aFilingOption?: Option;
  b13aNumber: string;
  eoriNumber: string;
  taxIdType: Option;
  taxIdNumber: string;
};

type JSONCustomsDeclarationPayload = {
  id: number;
  contents_type: string;
  contents_explanation: string;
  incoterm: string;
  exporter_reference: string;
  importer_reference: string;
  invoice: string;
  non_delivery_option: string;
  license: string;
  certificate: string;
  eel_pfc: string;
  aes_itn?: string;
  certify_signer: string;
  notes: string;
  is_certify: boolean;
  b13a_filing_option: string;
  b13a_number: string;
  eori_number: string;
  tax_id_type: string;
  tax_id_number: string;
};

type PIILessJSONCustomsDeclarationPayload = {
  id: number;
  contents_type: string;
  contents_explanation: string;
  incoterm: string;
  exporter_reference: boolean;
  importer_reference: boolean;
  invoice: boolean;
  non_delivery_option: string;
  license: string;
  certificate: boolean;
  eel_pfc: string;
  aes_itn?: boolean;
  certify_signer: boolean;
  notes: string;
  is_certify: boolean;
  b13a_filing_option: string;
  b13a_number: boolean;
  eori_number: boolean;
  tax_id_type: string;
  tax_id_number: boolean;
};

export type WebCustomsDeclaration = {
  /** Automated Export System Internal Transaction Number (exports from USA) */
  aes_itn: string;
  /** Name of the person who created the customs declaration and is responsible for the validity of all information provided */
  authorized_person: string;
  /** B13A Export Declaration, mandatory for Canadian exports valued at CAD$2000 or more */
  b13a_filing_option: 'FILED_ELECTRONICALLY' | 'NOT_REQUIRED' | 'SUMMARY_REPORTING';
  /** Proof of Report Number, Summary ID Number, or Exemption Number, depending on B13A filing option */
  b13a_number: string;
  certificate: string;
  /** Exemption and Exclusion Legend / Proof of Filing Citation (exports from USA) */
  eel_pfc: 'NOEEI_30_37_a' | 'NOEEI_30_37_h' | 'NOEEI_30_37_f' | 'NOEEI_30_36' | 'AES_ITN';
  exporter_reference: string;
  eori_number: string;
  importer_reference: string;
  incoterm: 'DDP' | 'DDU' | 'FCA';
  invoice_number: string;
  /** Expresses that the certify_signer has provided all information of this customs declaration truthfully */
  is_certified: boolean;
  is_vat_collected: boolean;
  items: WebCustomsItem[];
  license: string;
  metadata: string;
  non_delivery_handling: 'ABANDON' | 'RETURN';
  notes: string;
  /** Explanation of the type of goods of the shipment */
  purpose_explanation: string;
  /** Reason for export */
  purpose_of_shipment:
    | 'DOCUMENTS'
    | 'GIFT'
    | 'HUMANITARIAN_DONATION'
    | 'MERCHANDISE'
    | 'OTHER'
    | 'RETURN_MERCHANDISE'
    | 'SAMPLE';
  tax_id: string;
  tax_id_type: string;
};

function toJSONCustomsDeclarationPayload(
  customsDeclarationPayload: CustomsDeclarationPayload,
): JSONCustomsDeclarationPayload {
  return {
    id: customsDeclarationPayload.id,
    contents_type: customsDeclarationPayload.contentsType.value as string,
    contents_explanation: customsDeclarationPayload.contentsExplanation
      ? customsDeclarationPayload.contentsExplanation
      : '',
    incoterm: customsDeclarationPayload.incoterm.value as string,
    exporter_reference: customsDeclarationPayload.exporterReference,
    importer_reference: customsDeclarationPayload.importerReference,
    invoice: customsDeclarationPayload.invoice,
    non_delivery_option: customsDeclarationPayload.nonDeliveryOption.value as string,
    license: customsDeclarationPayload.license,
    certificate: customsDeclarationPayload.certificate,
    eel_pfc: customsDeclarationPayload.eelPfc
      ? (customsDeclarationPayload.eelPfc.value as string)
      : '',
    aes_itn: customsDeclarationPayload.aesItn ? customsDeclarationPayload.aesItn : '',
    certify_signer: customsDeclarationPayload.certifySigner,
    notes: customsDeclarationPayload.notes,
    is_certify: customsDeclarationPayload.isCertify,
    b13a_filing_option: (customsDeclarationPayload.b13aFilingOption?.value as string) || '',
    b13a_number: customsDeclarationPayload.b13aNumber || '',
    eori_number: customsDeclarationPayload.eoriNumber || '',
    tax_id_type: customsDeclarationPayload.taxIdType
      ? (customsDeclarationPayload.taxIdType?.value as string)
      : '',
    tax_id_number: customsDeclarationPayload.taxIdNumber || '',
  };
}

function prepareCustomsDeclarationSegmentData(
  customsDeclarationPayload: JSONCustomsDeclarationPayload,
): PIILessJSONCustomsDeclarationPayload {
  return {
    ...customsDeclarationPayload,
    exporter_reference: !!customsDeclarationPayload.exporter_reference,
    importer_reference: !!customsDeclarationPayload.importer_reference,
    invoice: !!customsDeclarationPayload.invoice,
    certificate: !!customsDeclarationPayload.certificate,
    certify_signer: !!customsDeclarationPayload.certify_signer,
    b13a_number: !!customsDeclarationPayload.b13a_number,
    eori_number: !!customsDeclarationPayload.eori_number,
    tax_id_number: !!customsDeclarationPayload.tax_id_number,
    aes_itn: !!customsDeclarationPayload.aes_itn,
  };
}

function fromJSON(jsonCustomsDeclaration: JSONCustomsDeclaration): CustomsDeclaration {
  return {
    id: jsonCustomsDeclaration.id,
    objectState: jsonCustomsDeclaration.object_state,
    exporterReference: jsonCustomsDeclaration.exporter_reference,
    importerReference: jsonCustomsDeclaration.importer_reference,
    contentsType: jsonCustomsDeclaration.contents_type,
    contentsExplanation: jsonCustomsDeclaration.contents_explanation,
    invoice: jsonCustomsDeclaration.invoice,
    notes: jsonCustomsDeclaration.notes,
    license: jsonCustomsDeclaration.license,
    certificate: jsonCustomsDeclaration.certificate,
    eelPfc: jsonCustomsDeclaration.eel_pfc,
    aesItn: jsonCustomsDeclaration.aes_itn,
    nonDeliveryOption: jsonCustomsDeclaration.non_delivery_option,
    isCertify: jsonCustomsDeclaration.is_certify,
    certifySigner: jsonCustomsDeclaration.certify_signer,
    disclaimer: jsonCustomsDeclaration.disclaimer,
    incoterm: jsonCustomsDeclaration.incoterm,
    metadata: jsonCustomsDeclaration.metadata,
    isCommercialInvoice: jsonCustomsDeclaration.is_commercial_invoice,
    isTest: jsonCustomsDeclaration.is_test,
    b13aFilingOption: B13AFilingOptionMap[jsonCustomsDeclaration.b13a_filing_option],
    b13aNumber: jsonCustomsDeclaration.b13a_number,
    eoriNumber: jsonCustomsDeclaration.exporter_identification.eori_number,
    taxIdType: jsonCustomsDeclaration.exporter_identification.tax_id.type,
    taxIdNumber: jsonCustomsDeclaration.exporter_identification.tax_id.number,
  };
}

export const fromWebCustomsDeclaration = (
  webCustomsDeclaration: WebCustomsDeclaration,
): CustomsDeclaration => {
  return {
    exporterReference: webCustomsDeclaration.exporter_reference,
    importerReference: webCustomsDeclaration.importer_reference,
    invoice: webCustomsDeclaration.invoice_number,
    notes: webCustomsDeclaration.notes,
    license: webCustomsDeclaration.license,
    certificate: webCustomsDeclaration.certificate,
    eelPfc: webCustomsDeclaration.eel_pfc,
    aesItn: webCustomsDeclaration.aes_itn,
    nonDeliveryOption: webCustomsDeclaration.non_delivery_handling,
    isCertify: webCustomsDeclaration.is_certified,
    incoterm: webCustomsDeclaration.incoterm,
    metadata: webCustomsDeclaration.metadata,
    b13aFilingOption: webCustomsDeclaration.b13a_filing_option,
    b13aNumber: webCustomsDeclaration.b13a_number,
    eoriNumber: webCustomsDeclaration.eori_number,
    taxIdNumber: webCustomsDeclaration.tax_id,
    taxIdType: webCustomsDeclaration.tax_id_type,
    contentsType: webCustomsDeclaration.purpose_of_shipment,
    contentsExplanation: webCustomsDeclaration.purpose_explanation,

    //nulls
    id: -1,
    objectState: undefined,
    certifySigner: '',
    disclaimer: undefined,
    isTest: false,
    isCommercialInvoice: false,
  };
};

function isDeclarationEqual(a: CustomsDeclaration, b: CustomsDeclaration) {
  return (
    a.contentsType === b.contentsType &&
    a.contentsExplanation === b.contentsExplanation &&
    a.exporterReference === b.exporterReference &&
    a.importerReference === b.importerReference &&
    a.invoice === b.invoice &&
    a.license === b.license &&
    a.certificate === b.certificate &&
    a.eelPfc === b.eelPfc &&
    a.aesItn === b.aesItn &&
    a.nonDeliveryOption === b.nonDeliveryOption &&
    a.isCertify === b.isCertify &&
    a.certifySigner === b.certifySigner &&
    a.notes === b.notes &&
    a.b13aFilingOption === b.b13aFilingOption &&
    a.b13aNumber === b.b13aNumber &&
    a.eoriNumber === b.eoriNumber &&
    a.taxIdType === b.taxIdType &&
    a.taxIdNumber === b.taxIdNumber
  );
}

export function requiresCustomsDeclaration(a: Address, b: Address | Maybe<APIAddress>) {
  if (!a || !b) return false;

  if (
    a.country &&
    a.country.iso2 === countries.US.iso2 &&
    b.country &&
    b.country.iso2 === countries.US.iso2
  ) {
    return isMilitaryAddress(a) || isMilitaryAddress(b) || isUSTerritory(a) || isUSTerritory(b);
  }

  return !!a.country && !!b.country && a.country.iso2 !== b.country.iso2;
}

function getCustomsPhoneErrors({
  addressFrom,
  addressTo,
}: {
  addressFrom: Address;
  addressTo?: Address;
}): { hasPhoneNumberRequiredError: boolean; hasPhoneNumberRequiredWarning: boolean } {
  const isCrossBorderShipment = requiresCustomsDeclaration(addressFrom, addressTo);
  const hasPhoneNumberRequiredError = isCrossBorderShipment && !addressFrom.phone;
  const hasPhoneNumberRequiredWarning = isCrossBorderShipment && !addressTo?.phone;

  return {
    hasPhoneNumberRequiredError,
    hasPhoneNumberRequiredWarning,
  };
}

export {
  JSONCustomsDeclaration,
  CustomsDeclaration,
  fromJSON,
  CustomsDeclarationPayload,
  JSONCustomsDeclarationPayload,
  PIILessJSONCustomsDeclarationPayload,
  toJSONCustomsDeclarationPayload,
  isDeclarationEqual,
  getCustomsPhoneErrors,
  prepareCustomsDeclarationSegmentData,
};
export default CustomsDeclaration;
